import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { logout } from '@/store/authSlice';
import type { RootState } from '@/store';
import { isApiResponse } from '@/types/api';
import { createAccessToken } from '@/utils/agent';
import { refreshAuthTokens } from '@/utils/authSession';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3001/api/v1';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers, { getState }) => {
    const { accessToken } = (getState() as RootState).auth;
    headers.set('Content-Type', 'application/json');
    headers.set('x-access-token', createAccessToken());
    if (accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    }
    return headers;
  },
});

function extractErrorMessage(data: unknown): string | null {
  if (typeof data === 'string') return data;
  if (data && typeof data === 'object' && 'message' in data) {
    const message = (data as { message: string | string[] }).message;
    if (Array.isArray(message) && message.length > 0) return String(message[0]);
    if (typeof message === 'string') return message;
  }
  if (data && typeof data === 'object' && 'errors' in data) {
    const errors = (data as { errors: string[] }).errors;
    if (Array.isArray(errors) && errors.length > 0) return String(errors[0]);
  }
  return null;
}

function normalizeError(error: FetchBaseQueryError): FetchBaseQueryError {
  const message = extractErrorMessage(error.data) ?? 'Something went wrong.';
  if (typeof error.status === 'number') {
    return { status: error.status, data: message };
  }
  return { status: 'CUSTOM_ERROR', error: message, data: message };
}

function unwrapResponse<T>(data: unknown): { payload: T; message?: string } {
  if (isApiResponse<T>(data)) {
    return { payload: data.data, message: data.message };
  }
  return { payload: data as T };
}

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  if (result.error?.status === 401) {
    const tokens = await refreshAuthTokens();

    if (tokens) {
      result = await rawBaseQuery(args, api, extraOptions);
    } else {
      api.dispatch(logout());
    }
  }

  if (result.error) {
    return { error: normalizeError(result.error) };
  }

  const { payload, message } = unwrapResponse(result.data);
  return {
    data: payload,
    ...(message ? { meta: { message } } : {}),
  };
};

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Auth',
    'Dashboard',
    'Products',
    'Orders',
    'Categories',
    'Subcategories',
    'Brands',
    'States',
    'Districts',
    'Assemblies',
    'Constituencies',
    'Attributes',
    'AttributeValues',
    'Banners',
    'Collections',
    'Media',
    'Coupons',
    'Inventory',
    'Reviews',
    'Customers',
    'Enquiries',
    'Contacts',
    'Settings',
    'Reports',
    'Cms',
    'Seo',
    'EmailTemplates',
    'Blogs',
    'Notifications',
    'Users',
  ],
  endpoints: () => ({}),
});
